import {
    getFeaturedProducts,
    getProductCategories,
    getProductCategory,
} from '@api/product';
import { Breadcrumb, BreadcrumbItem, BreadcrumbLink } from '@chakra-ui/react';
import { Container, Header, Section } from '@components';
import { ProductCardWithProvider, SmallCategoryCard } from '@components/cards';
import { SearchContainer } from '@components/container';
import { CarouselContainer } from '@components/container/carousel';
import { TProduct, TProductCategory } from '@components/types';
import { getSafeUrl } from '@core/utils';
import { GetServerSideProps } from 'next';
import dynamic from 'next/dynamic';
import Head from 'next/head';
import Link from 'next/link';
import { useRouter } from 'next/router';
import React, { useEffect, useState } from 'react';
import { SwiperSlide } from 'swiper/react';

interface Props {
  initialProductCategory?: TProductCategory;
  initialProductSubCategories?: TProductCategory[];
  initialProductList?: TProduct[];
}

export const getServerSideProps: GetServerSideProps<{
  initialProductCategory: TProductCategory;
  initialProductSubCategories: TProductCategory[];
  initialProductList: TProduct[];
}> = async (context) => {
  const { slug } = context.params;

  const [
    initialProductCategory,
    initialProductSubCategories,
    initialProductList,
  ] = await Promise.all([
    getProductCategory(parseInt(slug[0] as string)),
    getProductCategories(parseInt(slug[0] as string)),
    getFeaturedProducts(parseInt(slug[0] as string)),
  ]);

  return {
    props: {
      initialProductCategory: initialProductCategory,
      initialProductSubCategories: initialProductSubCategories,
      initialProductList: initialProductList,
    },
  };
};

const Navbar = dynamic(
  () => import('../../src/components/navbar').then((mod) => mod.Navbar),
  {
    ssr: false,
  }
);

const ProductList: React.FC = ({
  initialProductCategory,
  initialProductSubCategories,
  initialProductList,
}: Props) => {
  const router = useRouter();
  const [productCategory, setProductCategory] = useState<TProductCategory>(
    initialProductCategory
  );
  const [subCategories, setSubCategories] = useState<TProductCategory[]>(
    initialProductSubCategories
  );
  const [products, setProducts] = useState<TProduct[]>(initialProductList);
  const [pageNumber, setPageNumber] = useState(0);
  const [isLoadMore, setIsLoadMore] = useState(false);
  const pageSize = 20;

  const getAllFeaturedProducts = (extend: boolean = true) => {
    let id = (
      router.query.slug === undefined ? router.query.slug : router.query.slug[0]
    ) as string;
    if (id != undefined) {
      const resProducts: Promise<TProduct[]> = getFeaturedProducts(
        parseInt(id),
        null,
        null,
        null,
        pageNumber,
        pageSize
      );
      resProducts.then((result) => {
        setIsLoadMore(result.length == pageSize);
        if (extend) {
          setProducts([...products, ...result]);
        } else {
          setProducts(result);
        }
      });
    }
  };

  useEffect(() => {
    let id = (
      router.query.slug === undefined ? router.query.slug : router.query.slug[0]
    ) as string;
    if (!router.isReady || id == productCategory?.id.toString()) {
      return;
    }
    const res: Promise<TProductCategory> = getProductCategory(parseInt(id));
    res.then((productCategory) => {
      setProductCategory(productCategory);
    });
    const resSubCategories: Promise<TProductCategory[]> = getProductCategories(
      parseInt(id)
    );
    resSubCategories.then((categories) => setSubCategories(categories));
  }, [router.isReady, router.query.slug]);

  useEffect(() => {
    if (pageNumber == 0) {
      getAllFeaturedProducts(pageNumber != 0);
    } else {
      setPageNumber(0);
    }
  }, [router.isReady, router.query.slug]);

  useEffect(() => {
    getAllFeaturedProducts(pageNumber != 0);
  }, [pageNumber]);

  return (
    <Container>
      <Head>
        <title>{`${productCategory?.name} products - Jhattse`}</title>
        <meta
          name="Keywords"
          content={`${productCategory?.name} Products, Local Services,${productCategory?.name},Jhattse`}
        />
        <meta
          name="Description"
          content={`Get ${productCategory?.name} products from nearby local stores on Jhattse`}
        />
        <link
          rel="canonical"
          href={`https://jhattse.com/category/${
            productCategory?.id
          }/${getSafeUrl(productCategory?.name)}`}
        />
        <meta
          property="og:title"
          content={`${productCategory?.name} products on Jhattse`}
        />
        <meta
          name="og:description"
          content={`Get ${productCategory?.name} products from nearby local stores on Jhattse`}
        />
        <meta name="og:image" content={`${productCategory.image}`} />
        <meta
          property="og:url"
          content={`https://jhattse.com/category/${
            productCategory?.id
          }/${getSafeUrl(productCategory?.name)}`}
        />
      </Head>
      <Header />
      <div className="font-manrope w-full max-w-[1240px] mx-auto">
        <div className="hidden px-2 py-8 md:flex items-center font-manrope">
          <Breadcrumb className="text-base text-neutral-400 py-4 font-medium list-none text-breadcrumbs">
            <BreadcrumbItem>
              <BreadcrumbLink href="/">Home</BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbItem>
              <BreadcrumbLink href="/category">Category</BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbItem
              className="text-ellipsis text-left break-words line-clamp-1 select-none text-neutral-800 font-semibold"
              isCurrentPage
            >
              <BreadcrumbLink href={`/`}>
                {' '}
                {productCategory?.name}
              </BreadcrumbLink>
              <ol className="p-0 list-none"></ol>
            </BreadcrumbItem>
          </Breadcrumb>
        </div>
        <div className="px-2">
          <div className="py-2 grid grid-flow-col font-manrope">
            <div className="flex items-center justify-start">
              <h1 className="md:text-xl text-lg text-neutral-800 font-extrabold pb-2">
                {productCategory?.name}
              </h1>
            </div>
          </div>
          <div className="w-[80%] border-neutral-400 py-4 hidden md:block">
            <Section
              products={subCategories}
              element={SmallCategoryCard}
              intent="category"
              style="bg-white text-neutral-900"
              headStyle="p-2 font-medium text-base"
            />
          </div>
          <div className="block md:hidden pb-2">
            <CarouselContainer>
              {subCategories?.map((subCategory) => (
                <SwiperSlide style={{ width: 'auto' }} className="min-w-0">
                  <Link
                    href={`/category/${subCategory?.id}/${getSafeUrl(
                      subCategory?.name
                    )}`}
                  >
                    <div className="bg-neutral-50 border-neutral-300 border p-2 font-bold text-neutral-700 cursor-pointer border-solid rounded text-sm font-manrope">
                      {subCategory?.name}
                    </div>
                  </Link>
                </SwiperSlide>
              ))}
            </CarouselContainer>
          </div>
        </div>
        <div className="px-2 pb-20">
          <SearchContainer
            searchresults={products}
            element={ProductCardWithProvider}
            isLoadMore={isLoadMore}
            pageNumber={pageNumber}
            setPageNumber={setPageNumber}
          />
        </div>
      </div>
      <Navbar />
    </Container>
  );
};

export default ProductList;
